import React from "react";
import { useSearchParams } from "react-router-dom";
import RubricsTab from "./RubricsTab";
import FrameworksTab from "./FrameworksTab";

interface RubricsAndFrameworksPageProps {
  getToken: () => string | undefined;
}

type TabId = "rubrics" | "frameworks";

const TABS: { id: TabId; label: string }[] = [
  { id: "rubrics", label: "Rubrics" },
  { id: "frameworks", label: "Frameworks" },
];

/**
 * Tab state lives in the query string rather than component state (the way
 * AdminPage holds it) so a tab is linkable, survives a reload, and lets the
 * framework editor's Back land on the Frameworks tab instead of dumping the
 * user on Rubrics. Any unrecognised ?tab= value falls back to rubrics.
 */
const RubricsAndFrameworksPage: React.FC<RubricsAndFrameworksPageProps> = ({
  getToken,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab: TabId = requested === "frameworks" ? "frameworks" : "rubrics";

  const selectTab = (id: TabId) => {
    // replace: switching tabs is not a navigation the Back button should undo.
    // Note: `{}` wipes every query param, not just `tab`. Harmless today since
    // `/rubrics` carries no others, but the day a filter or search param is
    // added here, switching back to the rubrics tab will silently drop it too.
    setSearchParams(id === "rubrics" ? {} : { tab: id }, { replace: true });
  };

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-5xl w-full mx-auto px-8 py-10">
        <div className="flex gap-6 border-b border-outline-variant/30 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              className={`pb-3 -mb-px font-label text-xs uppercase tracking-widest transition-colors border-b-2 ${
                tab === t.id
                  ? "border-primary text-on-surface"
                  : "border-transparent text-slate-500 hover:text-on-surface"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "rubrics" ? (
          <RubricsTab getToken={getToken} />
        ) : (
          <FrameworksTab getToken={getToken} />
        )}
      </div>
    </div>
  );
};

export default RubricsAndFrameworksPage;
